import { useState } from 'react'
import { LayersControl, TileLayer, useMapEvents } from 'react-leaflet'

const FALLBACK_MIN_ZOOM = 1
const FALLBACK_MAX_ZOOM = 11

const offlineUrl = `${process.env.PUBLIC_URL}/tiles/{z}/{x}/{y}.png`

const BASE_LAYER_STORAGE_KEY = 'raycaster.mapBaseLayer'
const DEFAULT_BASE_LAYER = 'Google streets'

interface BaseLayerConfig {
  name: string
  url: string
  subdomains?: string[]
  minZoom?: number
  maxZoom?: number
}

const googleSubdomains = ['mt0', 'mt1', 'mt2', 'mt3']

const BASE_LAYERS: BaseLayerConfig[] = [
  {
    name: 'OSM streets',
    url: offlineUrl,
    minZoom: FALLBACK_MIN_ZOOM,
    maxZoom: FALLBACK_MAX_ZOOM,
  },
  {
    name: 'Google streets',
    url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    subdomains: googleSubdomains,
  },
  {
    name: 'Google satellite',
    url: 'https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
    subdomains: googleSubdomains,
  },
  {
    name: 'Google terrain',
    url: 'https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
    subdomains: googleSubdomains,
  },
]

function readStoredBaseLayer(): string {
  try {
    const raw = localStorage.getItem(BASE_LAYER_STORAGE_KEY)
    if (raw && BASE_LAYERS.some((layer) => layer.name === raw)) return raw
  } catch {}
  return DEFAULT_BASE_LAYER
}

const renderTileLayer = ({ name, subdomains, ...rest }: BaseLayerConfig) => (
  <TileLayer noWrap {...rest} {...(subdomains ? { subdomains } : {})} />
)

/** Base layers for use inside a <LayersControl>; persists the selected layer. */
export const BaseLayers = () => {
  const [initialLayer] = useState(readStoredBaseLayer)

  useMapEvents({
    baselayerchange: (e) => {
      try {
        localStorage.setItem(BASE_LAYER_STORAGE_KEY, e.name)
      } catch {}
    },
  })

  return (
    <>
      {BASE_LAYERS.map((layer) => (
        <LayersControl.BaseLayer
          key={layer.name}
          name={layer.name}
          checked={layer.name === initialLayer}
        >
          {renderTileLayer(layer)}
        </LayersControl.BaseLayer>
      ))}
    </>
  )
}

/** Single tile layer showing the user's last selected base layer (no switcher). */
export const SavedBaseTileLayer = () => {
  const [layerName] = useState(readStoredBaseLayer)
  const layer = BASE_LAYERS.find((l) => l.name === layerName) ?? BASE_LAYERS[1]
  return renderTileLayer(layer)
}
